/**
 * Shared widgets for the Layer 2 daily-entry forms.
 * Keeps the 6 pages DRY without yet another features/ folder.
 */
import { Card, Col, Row, Tag, Tooltip, Typography } from 'antd'
import {
  CheckCircleOutlined, EditOutlined, RobotOutlined, ExclamationCircleOutlined,
} from '@ant-design/icons'
import type { ReactNode } from 'react'

const { Text } = Typography

/** Tiny inline badge that flags how a value was sourced. Operator
 *  glances at the colour to know whether the field is auto-filled
 *  (green/system), manually typed (default), or an override. */
export type ValueSource = 'AUTO' | 'MANUAL' | 'OVERRIDE' | 'SYSTEM'

const SRC_TAG: Record<ValueSource, { color: string; icon: ReactNode; label: string }> = {
  AUTO:     { color: 'green',   icon: <RobotOutlined />, label: 'AUTO' },
  MANUAL:   { color: 'default', icon: <EditOutlined />, label: 'MANUAL' },
  OVERRIDE: { color: 'orange',  icon: <ExclamationCircleOutlined />, label: 'OVERRIDE' },
  SYSTEM:   { color: 'blue',    icon: <CheckCircleOutlined />, label: 'SYSTEM' },
}

export function SourceBadge({ source, tooltip }: { source: ValueSource; tooltip?: string }) {
  const cfg = SRC_TAG[source]
  return (
    <Tooltip title={tooltip || ''}>
      <Tag color={cfg.color} style={{ fontSize: 10, marginLeft: 6 }}>
        {cfg.icon} {cfg.label}
      </Tag>
    </Tooltip>
  )
}

/** Read-only field tile — used for auto-fetched values like Lot, Mixing,
 *  Laydown, Card sliver hank, Standard expected output, etc. */
export function ReadOnlyTile({
  label, value, hint, source = 'AUTO',
}: {
  label: string; value: ReactNode; hint?: string; source?: ValueSource
}) {
  return (
    <Card size="small" style={{ height: '100%', background: '#fafafa' }}>
      <div style={{ fontSize: 11, color: '#666', display: 'flex', alignItems: 'center' }}>
        <span>{label}</span>
        <SourceBadge source={source} tooltip={hint} />
      </div>
      <div style={{
        fontSize: 16, fontWeight: 600, fontVariantNumeric: 'tabular-nums',
        marginTop: 2,
      }}>
        {value ?? <Text type="secondary">—</Text>}
      </div>
      {hint && (
        <div style={{ fontSize: 10, color: '#999', marginTop: 2 }}>{hint}</div>
      )}
    </Card>
  )
}

/** Generated-cans summary card shown after Carding Production save. */
export function GeneratedCansSummary({
  cans,
}: {
  cans: Array<{
    can_no: string; net_kg: number; is_partial_can: boolean;
    qc_status: string; total_can_cost?: number | null;
  }>
}) {
  if (!cans || cans.length === 0) return null
  const total_kg = cans.reduce((s, c) => s + (c.net_kg || 0), 0)
  const total_value = cans.reduce((s, c) => s + (c.total_can_cost || 0), 0)
  const full = cans.filter(c => !c.is_partial_can).length
  const partial = cans.length - full

  return (
    <Card
      size="small"
      style={{
        marginTop: 16,
        background: '#f6ffed', borderColor: '#b7eb8f',
      }}
      title={
        <span>
          <CheckCircleOutlined style={{ color: '#52c41a' }} />{' '}
          <Text strong>Cans auto-generated — {cans.length}</Text>
        </span>
      }
    >
      <Row gutter={[8, 8]}>
        <Col xs={12} md={6}>
          <ReadOnlyTile label="Full cans" value={full} source="SYSTEM" />
        </Col>
        <Col xs={12} md={6}>
          <ReadOnlyTile label="Partial cans" value={partial} source="SYSTEM" />
        </Col>
        <Col xs={12} md={6}>
          <ReadOnlyTile
            label="Total kg"
            value={`${total_kg.toFixed(2)} kg`}
            source="SYSTEM"
          />
        </Col>
        <Col xs={12} md={6}>
          <ReadOnlyTile
            label="Total value"
            value={total_value > 0 ? `₹${total_value.toFixed(2)}` : 'PENDING'}
            source="SYSTEM"
            hint={total_value === 0 ? 'Cost calc pending — laydown RM cost not cached yet' : undefined}
          />
        </Col>
      </Row>
      <div style={{
        marginTop: 8, padding: 8, background: '#fff',
        borderRadius: 4, border: '1px solid #d9d9d9',
        fontSize: 11, fontFamily: 'monospace',
        maxHeight: 120, overflowY: 'auto',
      }}>
        {cans.slice(0, 30).map(c => (
          <div key={c.can_no} style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>{c.can_no}{c.is_partial_can ? ' (partial)' : ''}</span>
            <span>
              {c.net_kg.toFixed(2)} kg{' '}
              <Tag style={{ fontSize: 9, margin: 0 }}>{c.qc_status}</Tag>
            </span>
          </div>
        ))}
        {cans.length > 30 && (
          <div style={{ color: '#999' }}>… {cans.length - 30} more</div>
        )}
      </div>
    </Card>
  )
}

/** Status pill for QC / movement state — used in the QC list views. */
export function StatusPill({ status }: { status: string }) {
  const map: Record<string, { color: string; label?: string }> = {
    PENDING:                  { color: 'default' },
    PASSED:                   { color: 'green' },
    HELD:                     { color: 'orange' },
    HELD_BY_QC:               { color: 'orange', label: 'QC HOLD' },
    RELEASED:                 { color: 'cyan' },
    REJECTED:                 { color: 'red' },
    READY_FOR_DRAW_FRAME:     { color: 'blue', label: 'READY' },
    TRANSFERRED_TO_DRAW_FRAME:{ color: 'purple', label: 'TRANSFERRED' },
    IN_CARDING:               { color: 'default', label: 'IN CARDING' },
    CONSUMED:                 { color: 'magenta' },
    AUTO_CLOSED:              { color: 'green', label: 'AUTO CLOSED' },
    FLAGGED_MINOR:            { color: 'gold', label: 'MINOR' },
    PENDING_INVESTIGATION:    { color: 'red', label: 'INVESTIGATE' },
    PENDING_QC_INVESTIGATION: { color: 'red', label: 'QC INVESTIGATE' },
  }
  const cfg = map[status] || { color: 'default' }
  return <Tag color={cfg.color} style={{ margin: 0 }}>{cfg.label || status}</Tag>
}
